// React hook for live speech-to-text backed by Amazon Transcribe streaming.
//
// Usage:
//   const stt = useSpeechToText({
//     onFinalText: (text) => appendToInput(text),
//   });
//   stt.toggle();            // start / stop recording
//   stt.interimText;         // live partial transcript for preview
//   stt.isRecording;         // boolean
//
// Finalized utterances are delivered through onFinalText so the caller decides
// how they land in the input (append, replace, etc.). The interim transcript is
// exposed for an in-progress preview and is reset once recording stops.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  startTranscription,
  isSpeechToTextSupported,
  getTranscribeConfig,
} from "../services/amazonTranscribe";

export default function useSpeechToText({ onFinalText } = {}) {
  const [isRecording, setIsRecording] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [interimText, setInterimText] = useState("");
  const [error, setError] = useState(null);

  const sessionRef = useRef(null);
  const onFinalTextRef = useRef(onFinalText);
  const mountedRef = useRef(true);

  useEffect(() => {
    onFinalTextRef.current = onFinalText;
  }, [onFinalText]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      sessionRef.current?.stop();
      sessionRef.current = null;
    };
  }, []);

  const config = getTranscribeConfig();
  const isConfigured = isSpeechToTextSupported() && !!config.identityPoolId;

  const stop = useCallback(async () => {
    const session = sessionRef.current;
    sessionRef.current = null;
    if (session) await session.stop();
    if (mountedRef.current) {
      setIsRecording(false);
      setIsConnecting(false);
      setInterimText("");
    }
  }, []);

  const start = useCallback(async () => {
    if (sessionRef.current || isConnecting) return;
    setError(null);
    setInterimText("");
    setIsConnecting(true);
    try {
      const session = await startTranscription({
        onTranscript: ({ transcript, isFinal }) => {
          if (!mountedRef.current) return;
          if (isFinal) {
            setInterimText("");
            onFinalTextRef.current?.(transcript);
          } else {
            setInterimText(transcript);
          }
        },
        onError: (err) => {
          if (!mountedRef.current) return;
          setError(err);
          stop();
        },
        onAutoStop: () => {
          // Session already tore itself down after a silence timeout; just
          // reset UI state so the mic button returns to idle.
          sessionRef.current = null;
          if (!mountedRef.current) return;
          setIsRecording(false);
          setIsConnecting(false);
          setInterimText("");
        },
      });
      if (!mountedRef.current) {
        await session.stop();
        return;
      }
      sessionRef.current = session;
      setIsRecording(true);
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    } finally {
      if (mountedRef.current) setIsConnecting(false);
    }
  }, [isConnecting, stop]);

  const toggle = useCallback(() => {
    if (sessionRef.current) stop();
    else start();
  }, [start, stop]);

  return {
    isSupported: isConfigured,
    isRecording,
    isConnecting,
    interimText,
    error,
    start,
    stop,
    toggle,
  };
}
